import dayjs from 'dayjs';

const makeCategoryId = category => `cat:${category}`;
const makeSubCategoryId = (parent, sub) => `sub:${parent}:${sub}`;
const makeEntryId = (parent, sub, entry) => `entry:${parent}:${sub}:${entry}`;

const isCategoryId = id => id.startsWith('cat:');
const isSubCategoryId = id => id.startsWith('sub:');
const isEntryId = id => id.startsWith('entry:');

const getOrderedFiscalMonths = firstFiscalMonth => {
  const defaultMonths = [...Array(12)].map((_, i) => i + 1);
  if (firstFiscalMonth === 1) {
    return {previousYearMonths: [], currentYearMonths: defaultMonths};
  }
  const monthsFromFirstFiscalMonth = defaultMonths.splice(firstFiscalMonth);
  const monthsBeforeFirstFiscalMonth = defaultMonths.splice(0, firstFiscalMonth);

  return {
    previousYearMonths: monthsFromFirstFiscalMonth,
    currentYearMonths: monthsBeforeFirstFiscalMonth
  };
};

const getFiscalYearColumnsRaw = (firstFiscalMonth, year) => {
  const orderedMonths = getOrderedFiscalMonths(firstFiscalMonth);
  const previousYear = (year - 1).toString();

  const fmt = (month, y) => `${y}-${month < 10 ? `0${month}` : month}`;

  const currentYearColumns = orderedMonths.currentYearMonths.map(m => fmt(m, year));
  const previousYearColumns = orderedMonths.previousYearMonths.map(m => fmt(m, previousYear));
  return [...previousYearColumns, ...currentYearColumns];
};

const getUniqueCategoriesRaw = data => {
  const ordered = [...data].sort((a, b) => (a.racine3_order ?? 0) - (b.racine3_order ?? 0));
  return [...new Set(ordered.map(d => d.racine3_name))];
};

const getUniqueSubCategoriesRaw = data => {
  const subCats = [...new Set(data.map(d => d.racine3_subname))];
  return subCats
    .filter(s => s !== null && s !== undefined)
    .map(subCategory => {
      const parent = data.find(d => d.racine3_subname === subCategory)?.racine3_name ?? null;
      return {parentCategory: parent, subCategory};
    });
};

const getEntriesByRowLabelAndDepthRaw = (data, rowLabel, depth = 3) => {
  if (depth === 1) return data.filter(e => e.racine3_name === rowLabel);
  if (depth === 2) return data.filter(e => e.racine3_subname === rowLabel);
  return data.filter(e => e.label === rowLabel);
};

const getOneAccountingEntryAmountByMonthRaw = (entry, columnId) => (entry?.[columnId] ? parseFloat(entry[columnId]) : 0);

const getMonthCellTotalRaw = (data, rowLabel, columnId, rowDepth = null) => {
  const entries = getEntriesByRowLabelAndDepthRaw(data, rowLabel, rowDepth);
  return entries.reduce((acc, entry) => acc + getOneAccountingEntryAmountByMonthRaw(entry, columnId), 0);
};

const getYearCellTotalRaw = ({data, yearColumns, rowLabel, rowDepth = null}) => {
  return yearColumns.reduce((acc, col) => acc + getMonthCellTotalRaw(data, rowLabel, col, rowDepth), 0);
};

const getTotalColumnCssClassesRaw = (total, isYear) => {
  const baseCssClass = isYear ? 'bold cell-total-year' : 'bold cell-total-month';
  const negativeNumberCssClass = total < 0 ? 'negative' : '';
  return `${baseCssClass} ${negativeNumberCssClass}`.trim();
};

const getMonthTotalCellCssClassesRaw = (rowId, rowDepth, monthTotal, expandedRows) => {
  const isCalculated = rowDepth === 1 || rowDepth === 2;
  const baseCss = isCalculated ? 'cell-total-month' : '';
  const negativeCss = monthTotal < 0 ? 'negative' : '';
  let bgCss = '';
  let boldCss = '';
  if (rowDepth === 3) {
    bgCss = rowId % 2 === 0 ? 'constrast-bg-cell' : 'white-bg-cell';
  } else {
    boldCss = expandedRows.includes(rowId) ? 'bold' : '';
  }
  return `${baseCss} ${boldCss} ${bgCss} ${negativeCss}`.trim();
};

const getYearTotalCellCssClassesRaw = (year, yearTotal, expandedYears) => {
  const baseCss = 'cell-total-year';
  const boldCss = expandedYears.includes(year) ? 'bold' : '';
  const negativeCss = yearTotal < 0 ? 'negative' : '';
  return `${baseCss} ${boldCss} ${negativeCss}`.trim();
};

const getHeaderRowCellsRaw = (budgetYears, expandedColumns, firstFiscalMonth, areRatiosActivated) => {
  const firstCell = {type: 'header', text: 'Libellés', className: 'first-column-title'};
  let cells = [firstCell];

  // eslint-disable-next-line no-restricted-syntax
  for (const year of budgetYears) {
    const isYearExpanded = expandedColumns.includes(year);
    const ratioId = `Ratio ${year}`;
    let monthsCells = [];

    if (isYearExpanded) {
      const columns = getFiscalYearColumnsRaw(firstFiscalMonth, year);
      monthsCells = columns.map(columnName => ({
        type: 'chevron',
        nonEditable: true,
        text: columnName,
        parentId: year,
        className: 'cell-header-budget-month'
      }));
    }

    if (areRatiosActivated) {
      monthsCells.unshift({
        type: 'chevron',
        nonEditable: true,
        hasChildren: true,
        isExpanded: isYearExpanded,
        text: ratioId,
        className: 'cell-header-budget cell-header-budget-ratio'
      });
    }

    monthsCells.unshift({
      type: areRatiosActivated ? 'text' : 'chevron',
      hasChildren: !areRatiosActivated,
      nonEditable: true,
      isExpanded: !areRatiosActivated ? isYearExpanded : false,
      text: year,
      className: 'cell-header-budget cell-header-budget-year'
    });

    cells = [...cells, ...monthsCells];
  }
  return cells;
};

const getBudgetHeaderRowRaw = (budgetYears, expandedColumns, firstFiscalMonth, isRatiosColumnVisible) => ({
  rowId: 'header',
  height: 40,
  cells: getHeaderRowCellsRaw(budgetYears, expandedColumns, firstFiscalMonth, isRatiosColumnVisible)
});

const getColumnsNamesRaw = (budgetYears, expandedColumns, firstFiscalMonth, isRatiosColumnVisible) => {
  let columnsNames = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const year of budgetYears) {
    let yearColumns = [];
    if (expandedColumns.includes(year)) {
      yearColumns = getFiscalYearColumnsRaw(firstFiscalMonth, year);
    }
    if (isRatiosColumnVisible) {
      yearColumns.unshift(year); // ratios column id
    }
    yearColumns.unshift(year); // year total column id
    columnsNames = [...columnsNames, ...yearColumns];
  }
  columnsNames.unshift('Libellés');

  return columnsNames;
};

// Simple percentage formatter to replace getRoundedPercentage
const getRoundedPercentageRaw = ratio => {
  // ratio is 0..N (e.g., 0.25 = 25%)
  const pct = ratio * 100;
  // round to one decimal like your UI suggests
  return Math.round(pct * 10) / 10;
};

// Fallback "addContextMenuOption" if you don’t want to import it
const addContextMenuOptionRaw = (options, id, label, handler) => [...options, {id, label, handler}];

/* ---------- Facade factory ---------- */

const createBudgetModel = (data, firstFiscalMonth, expandedColumns, expandedRows, budgetYears) => {
  // Pre-bind where it helps
  return {
    // Structure / meta
    getCategories: () => getUniqueCategoriesRaw(data),
    getSubCategories: () => getUniqueSubCategoriesRaw(data),
    hasSubCategories: parentCategory => getUniqueSubCategoriesRaw(data).some(c => c.parentCategory === parentCategory),

    // Columns & header
    getFiscalYearColumns: year => getFiscalYearColumnsRaw(firstFiscalMonth, year),
    getColumnsNames: isRatiosColumnVisible => getColumnsNamesRaw(budgetYears, expandedColumns, firstFiscalMonth, isRatiosColumnVisible),
    getBudgetHeaderRow: isRatiosColumnVisible => getBudgetHeaderRowRaw(budgetYears, expandedColumns, firstFiscalMonth, isRatiosColumnVisible),

    // Totals
    getMonthCellTotal: (rowLabel, columnId, rowDepth = null) => getMonthCellTotalRaw(data, rowLabel, columnId, rowDepth),
    getYearCellTotalForRow: (rowLabel, year, rowDepth = null) => {
      const yearColumns = getFiscalYearColumnsRaw(firstFiscalMonth, year);
      return getYearCellTotalRaw({data, yearColumns, rowLabel, rowDepth});
    },

    // Global totals (bottom row)
    getYearColumnTotalAllEntries: year => {
      // Sum all numeric properties starting with `${year}-`
      return data.reduce((acc, entry) => {
        const props = Object.keys(entry).filter(k => k.startsWith(`${year}-`));
        const sumForEntry = props.reduce((s, p) => s + (entry[p] ? parseFloat(entry[p]) : 0), 0);
        return acc + sumForEntry;
      }, 0);
    },
    getMonthColumnTotalAllEntries: columnId => {
      const monthEntries = data.filter(e => e[columnId] !== null && e[columnId] !== undefined);
      return monthEntries.reduce((acc, e) => acc + parseFloat(e[columnId] || 0), 0);
    },

    // Styling helpers (pre-bound to expandedRows/expandedColumns)
    getYearTotalCellCssClasses: (year, yearTotal) => getYearTotalCellCssClassesRaw(year, yearTotal, expandedColumns),
    getMonthTotalCellCssClasses: (rowId, rowDepth, monthTotal) => getMonthTotalCellCssClassesRaw(rowId, rowDepth, monthTotal, expandedRows),
    getTotalColumnCssClasses: (total, isYear) => getTotalColumnCssClassesRaw(total, isYear),

    // Misc
    addContextMenuOption: addContextMenuOptionRaw,
    roundedPercentage: getRoundedPercentageRaw
  };
};

const getPossibleBudgetYears = alreadyExistingBudgetYears => {
  let possibleBudgetYears = [];
  // Business rule : user can create a budget for year n+3 max and n-1 min
  for (let i = 0; i < 5; i++) {
    const dateYear = dayjs().year() - 1;
    const y = dateYear + i;
    possibleBudgetYears.push(y);
  }
  possibleBudgetYears = possibleBudgetYears.filter(y => !alreadyExistingBudgetYears.includes(y.toString()));
  return possibleBudgetYears;
};

const getDistinctBudgetYearsFromData = (data, budgetName) => {
  if (data && data.length > 0) {
    const years = new Set();

    const selectedBudgetData = data.filter(item => item.budget_name === budgetName);

    selectedBudgetData.forEach(item => {
      item.budget_fy_list.forEach(date => {
        years.add(date.slice(0, 4));
      });
    });

    return Array.from(years).sort();
  }

  return [];
};

export {createBudgetModel, getPossibleBudgetYears, getDistinctBudgetYearsFromData, makeCategoryId, makeSubCategoryId, makeEntryId, isCategoryId, isSubCategoryId, isEntryId};
